import styled from "styled-components";
import { CaretLeftOutlined, CaretRightOutlined } from "@ant-design/icons";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
} from "@/common/styles";
import { StyledActionPreview, StyledBookingsDivider } from "../styles";
import Button from "@/common/elements/Button";
import InmateFlag, { INMATE_FLAG_TYPE } from "@/components/InmateFlag";
import Alert from "@/common/elements/Alerts";
import CustomModal from "@/common/elements/Modal";
import { useState, useEffect, Suspense } from "react";
import FlagInformationDetails from "./FlagInformationDetails";
import PersonalInformationDetails from "../PersonalInformationDetails";
import BookingAdministrativeInformation from "../BookingAdministrativeInformation";
import FolderDataTable from "@/components/FolderDetailSearch/FolderDataTable";
import { useDispatch, useSelector } from "react-redux";
import { PHOTOS_BASE_URL } from "@/util/api";
import { Image } from "antd";
import defaultAvatar from "@/assets/default_avatar.svg";
import {
  isNonEmptyVariable,
  getRefListItemsbyName,
} from "@/util/common_function";
import dayjs from "dayjs";
import Table from "@/common/elements/Table";
import { TableHeader } from "@/components/InmateDetails/styles";
import BookingsPage from "@/pages/Bookings";
import Modal from "@/common/elements/Modal";
import BookingsDetails from "..";
import Staffing from "@/pages/Staffing";
import {
  setBookingsFieldsEnabled,
  setShowBookings,
} from "@/slices/bookingsSlice";

const StyledFolderDetails = styled.div`
  .profile-link {
    text-decoration: underline;
    font-size: 16px;
    padding: 0;
    background: transparent !important;
    color: var(--text-accent);
    &:hover {
      color: var(--text-accent-hover);
      background: transparent !important;
    }
  }
`;

// Helper function: from an array of cdInvolvements, return only the most relevant record for a given flag type.
const getRelevantInvolvements = (cdInvolvements, flagTypeConstant) => {
  if (!cdInvolvements || !cdInvolvements.length) return [];

  const filterFT = getRefListItemsbyName("FlagTypes")?.filter((f) => {
    return f?.constantName === flagTypeConstant;
  });
  if (!filterFT || !filterFT.length) return [];

  const refId = filterFT[0].refListItemGlobalId;
  const filtered = cdInvolvements.filter((inv) => {
    return (
      inv.flagTypeID === refId ||
      inv.dangerTypeId === refId ||
      inv.gangTypeId === refId ||
      inv.medicalTypeId === refId ||
      inv.noteTypeId === refId ||
      inv.civicTypeId === refId
    );
  });
  if (!filtered.length) return [];
  return filtered;
};

const FolderFullDetails = ({
  showFoldersList = true,
  onFolderSelected,
  selectedFolderTemp,
  associates,
  parentKey,
}) => {
  // Grab bookings from redux.
  const reduxBookings = useSelector((state) => state?.bookings?.bookings);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [bioPhotoUrl, setBioPhotoUrl] = useState("");
  const [bioPhotos, setBioPhotos] = useState([]);
  const [bioName, setBioName] = useState("New Folder");
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showBioPhotoIndex, setShowBioPhotoIndex] = useState(-1);
  const [showModal, setShowModal] = useState(false);
  const dispatch = useDispatch();

  const [header, setHeaders] = useState([
    { key: "bookingId", text: "Booking ID" },
    { key: "dateAdded", text: "Date Created" },
    { key: "timeAdded", text: "Time Created" },
    { key: "arrestDate", text: "Arrest Date" },
    { key: "releaseDate", text: "Release Date" },
    { key: "housed", text: "Housed" },
    { key: "caseNumber", text: "Case Number" },
    { key: "offenseDate", text: "Offense Date" },
  ]);
  useEffect(() => {
    if (parentKey == "staff") {
      setHeaders([
        { key: "staffId", text: "ID" },
        { key: "dateAdded", text: "Date Created" },
        { key: "rank", text: "Rank" },
        { key: "division", text: "Division" },
        { key: "badge", text: "Badge" },
      ]);
    }
  }, [parentKey]);
  const bookingsArray = Array.isArray(reduxBookings)
    ? reduxBookings
    : [reduxBookings];

  console.log({ bookingsArray });
  const handleInfoModalVisible = () => {
    setShowInfoModal(!showInfoModal);
  };

  useEffect(() => {
    if (associates) {
      setSelectedFolder(selectedFolderTemp);
    }
  }, [selectedFolderTemp]);

  // When a row in the FolderDataTable is clicked,
  // update local state and inform the parent if needed.
  const handleRowClick = (rowData) => {
    setSelectedFolder(rowData);
    if (onFolderSelected) {
      onFolderSelected(rowData);
    }
  };

  useEffect(() => {
    const bioData = associates
      ? selectedFolder?.originalData?.jackets?.bio?.[0]
      : reduxBookings?.bio?.[0];

    if (bioData) {
      setBioName(
        bioData.lName ? `${bioData.lName} ${bioData.fName}` : "New Folder"
      );
      const photos = bioData.bioPhotos || [];
      setBioPhotos(photos);
      if (
        photos.length > 0 &&
        photos[0].photos &&
        photos[0].photos[0]?.fullSizePath
      ) {
        setBioPhotoUrl(
          PHOTOS_BASE_URL + getFinalPath(photos[0].photos[0].fullSizePath)
        );
        setShowBioPhotoIndex(0);
      } else {
        setBioPhotoUrl(defaultAvatar);
      }
    } else {
      setBioName("New Folder");
      setBioPhotos([]);
      setBioPhotoUrl(defaultAvatar);
      setShowBioPhotoIndex(-1);
    }
  }, [reduxBookings, selectedFolder, associates]);

  const getFinalPath = (path) => {
    if (path?.includes("\\")) {
      const splitPath = path.split("/");
      if (splitPath.length === 4) {
        splitPath.shift();
        splitPath.shift();
      }
      return splitPath.join("\\");
    }
    return path;
  };

  const handleImageError = () => {
    setBioPhotoUrl(defaultAvatar);
  };

  const prevBioPhoto = () => {
    if (showBioPhotoIndex > 0) {
      setShowBioPhotoIndex(showBioPhotoIndex - 1);
    }
  };

  const nextBioPhoto = () => {
    if (bioPhotos && showBioPhotoIndex < bioPhotos.length - 1) {
      setShowBioPhotoIndex(showBioPhotoIndex + 1);
    }
  };

  useEffect(() => {
    if (bioPhotos.length > 0 && showBioPhotoIndex !== -1) {
      const currentPhoto = bioPhotos[showBioPhotoIndex];
      if (
        currentPhoto &&
        currentPhoto.photos &&
        currentPhoto.photos[0]?.fullSizePath
      ) {
        setBioPhotoUrl(
          PHOTOS_BASE_URL + getFinalPath(currentPhoto.photos[0].fullSizePath)
        );
      } else {
        setBioPhotoUrl(defaultAvatar);
      }
    }
  }, [bioPhotos, showBioPhotoIndex]);

  const renderFlag = (flagType) => {
    const cdInvolvements = associates
      ? selectedFolder?.originalData?.cdInvolvements
      : reduxBookings?.cdInvolvements;
    const relevantInvolvements = getRelevantInvolvements(
      cdInvolvements,
      flagType
    );
    const flagEnabled = relevantInvolvements.length > 0;
    return (
      <InmateFlag
        key={flagType}
        type={flagType}
        flagEnabled={flagEnabled}
        cdInvolvements={relevantInvolvements}
      />
    );
  };

  return (
    <>
      {(selectedFolder || !associates) && (
        <StyledFolderDetails>
          <StyledGridRow gutter={16}>
            <StyledGridCol xs={6}>
              <StyledActionPreview>
                <Image
                  src={bioPhotoUrl}
                  alt="Default Inmate Avatar"
                  width="100%"
                  onError={handleImageError}
                  fallback={defaultAvatar}
                />
                <div className="action-pagination">
                  <Button
                    type="text"
                    color="default"
                    variant="text"
                    onClick={prevBioPhoto}
                  >
                    <CaretLeftOutlined />
                  </Button>
                  <Button
                    type="text"
                    color="default"
                    variant="text"
                    onClick={nextBioPhoto}
                  >
                    <CaretRightOutlined />
                  </Button>
                  {bioPhotos.length ? (
                    <div className="pages">
                      {showBioPhotoIndex + 1}/{bioPhotos.length}
                    </div>
                  ) : (
                    ""
                  )}
                </div>
              </StyledActionPreview>
            </StyledGridCol>
            <StyledGridCol xs={18}>
              <StyledGridRow gutter={16} justify="space-between" align="middle">
                <StyledGridCol>
                  {isNonEmptyVariable(associates) ? (
                    <p className="text-dark fw-bold">{bioName}</p>
                  ) : (
                    <Button
                      type="text"
                      variant="text"
                      className="profile-link"
                      onClick={handleInfoModalVisible}
                    >
                      {bioName}
                    </Button>
                  )}
                </StyledGridCol>
                <StyledGridCol style={{ padding: "0px 30px" }}>
                  <StyledFlex justify="start" style={{ position: "relative" }}>
                    {renderFlag(INMATE_FLAG_TYPE.WARRANT)}
                    {renderFlag(INMATE_FLAG_TYPE.CIVIC)}
                    {renderFlag(INMATE_FLAG_TYPE.DANGER)}
                    {renderFlag(INMATE_FLAG_TYPE.INJAIL)}
                    {renderFlag(INMATE_FLAG_TYPE.GANG)}
                    {renderFlag(INMATE_FLAG_TYPE.MEDICAL)}
                    {renderFlag(INMATE_FLAG_TYPE.NOTES)}
                  </StyledFlex>
                </StyledGridCol>
                <StyledGridCol lg={24}>
                  <StyledBookingsDivider
                    style={{ marginBottom: "15px", marginTop: "15px" }}
                  />
                </StyledGridCol>
              </StyledGridRow>
              <FlagInformationDetails
                flagDetailsSource={
                  associates
                    ? selectedFolder?.originalData?.jackets?.bio?.[0]
                    : reduxBookings?.bio?.[0]
                }
                associates={associates}
              />
              <Alert
                type="info"
                show={true}
                style={{ marginTop: "10px", marginBottom: "25px" }}
                message={`The current record is in the folder named ${bioName}. If you want to move this record to a different folder or create a new folder, 
                click the 'Search for folder' button. You should make every effort to identify an existing folder for this person and put this record into that folder.`}
              />
            </StyledGridCol>
          </StyledGridRow>
        </StyledFolderDetails>
      )}
      {showFoldersList && <FolderDataTable onRowClick={handleRowClick} />}
      {showInfoModal && (
        <CustomModal
          header={bioName ? `${bioName} Information` : "New Folder"}
          show={showInfoModal}
          size="xl"
          onHide={handleInfoModalVisible}
          // footerActions={[{ key: "save", text: "Save" }]}
          footerCloseAction
          footerCloseActionText="Close"
          footerActionsNeed={false}
        >
          <StyledSectionHeading>
            <span>Personal Information</span>
          </StyledSectionHeading>
          <PersonalInformationDetails />
          <StyledSectionHeading>
            <span>{parentKey == "staff" ? "Staff" : "Bookings"} Records</span>
          </StyledSectionHeading>
          <Table
            headers={header || []}
            data={
              parentKey == "staff"
                ? bookingsArray.map((booking) => ({
                    staffId: booking?.staffId,
                    dateAdded: booking?.dateAdded,
                    rank: booking?.rankHr[0]?.rank,
                    division: booking?.organization[0]?.division,
                    badge: booking?.badge,
                  }))
                : bookingsArray.map((booking) => ({
                    bookingId: booking?.bookingId || booking?.staffId,
                    dateAdded: booking?.dateAdded,
                    timeAdded: booking?.timeAdded,
                    arrestDate: booking?.arrestDate,
                    releaseDate: booking?.releaseDate,
                    housed: booking?.housed,
                    caseNumber: booking?.caseNumber,
                    offenseDate: booking?.offenseDate,
                  }))
            }
            onRowClick={() => {
              setShowModal(true);
            }}
          />
          <TableHeader>
            <StyledFlex
              gap="10px"
              style={{
                padding: "5px",
              }}
            >
              <Button>Details</Button>
              <Button>Config</Button>
            </StyledFlex>
          </TableHeader>
          <StyledSectionHeading>
            <span>Administrative Information</span>
          </StyledSectionHeading>
          <BookingAdministrativeInformation />
        </CustomModal>
      )}
      {showModal && (
        <>
          {parentKey == "staff" ? (
            <Modal
              designPattern="ant"
              header={"Staff"}
              show={showModal}
              onHide={() => setShowModal(false)}
              size="xxl"
              backdrop="static"
              footerActionsNeed={false}
              maskClosable={false}
              rootClassName="custom-modal-mask-dark"
              // footerCloseAction
              // footerCloseActionText="Cancel"
              width={"95%"}
            >
              <Suspense
                fallback={<p>Loading...</p>}
                style={{ minHeight: "300px" }}
              >
                <Staffing fromFolderDetails={true} />
              </Suspense>
            </Modal>
          ) : (
            <Modal
              designPattern="ant"
              header={"Bookings"}
              show={showModal}
              onHide={() => setShowModal(false)}
              size="xxl"
              backdrop="static"
              footerActionsNeed={false}
              maskClosable={false}
              rootClassName="custom-modal-mask-dark"
              // footerCloseAction
              // footerCloseActionText="Cancel"
              width={"95%"}
            >
              <Suspense
                fallback={<p>Loading...</p>}
                style={{ minHeight: "300px" }}
              >
                <BookingsPage
                  isBQ={true}
                  nextBookingStep={null}
                  bqCompleteSaveAction={() => {}}
                  fromBookingStep={true}
                  fromFolderDetails={true}
                />
              </Suspense>
            </Modal>
          )}
        </>
      )}
    </>
  );
};

export default FolderFullDetails;
